import Papa from "papaparse";

/**
 * PapaParse wrapper for reading PSA catalog CSVs entirely in the browser.
 * Strips a UTF-8 BOM from headers, streams files through a worker above a size
 * threshold with progress events, and returns plain string-keyed rows for the
 * adapter layer.
 */

export interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
}

export interface ParseOptions {
  /** Called with parse progress in `[0, 1]` (always reaches 1 on success). */
  onProgress?: (fraction: number) => void;
  /** Files larger than this (bytes) are streamed via a worker. */
  workerThresholdBytes?: number;
}

/** Default size above which a file is streamed through a PapaParse worker (5 MB). */
export const DEFAULT_WORKER_THRESHOLD_BYTES = 5 * 1024 * 1024;

const STRIP_BOM = /^\uFEFF/;

const transformHeader = (header: string): string => header.replace(STRIP_BOM, "").trim();

/**
 * Parse a CSV `File` into headers and string rows. The file is streamed in
 * chunks (so progress can be reported and the UI thread stays responsive);
 * files above {@link DEFAULT_WORKER_THRESHOLD_BYTES} additionally run in a
 * worker. A leading BOM is stripped from header names.
 *
 * @throws {Error} If the file has no header row, or PapaParse reports a fatal error.
 */
export function parseCsv(file: File, options: ParseOptions = {}): Promise<ParsedCsv> {
  const threshold = options.workerThresholdBytes ?? DEFAULT_WORKER_THRESHOLD_BYTES;
  const useWorker = file.size > threshold;
  return new Promise((resolve, reject) => {
    const rows: Record<string, string>[] = [];
    let headers: string[] = [];
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: "greedy",
      worker: useWorker,
      transformHeader,
      chunk: (results) => {
        if (headers.length === 0 && results.meta.fields) {
          headers = results.meta.fields;
        }
        for (const row of results.data) {
          rows.push(row);
        }
        const cursor = results.meta.cursor;
        if (options.onProgress && cursor) {
          options.onProgress(Math.min(cursor / file.size, 1));
        }
      },
      complete: () => {
        if (headers.length === 0) {
          reject(new Error("No header row found in the CSV file."));
          return;
        }
        options.onProgress?.(1);
        resolve({ headers, rows });
      },
      error: (error: Error) => reject(error),
    });
  });
}

/** Parse an in-memory CSV string (used for programmatic input and tests). */
export function parseText(text: string, options: ParseOptions = {}): Promise<ParsedCsv> {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<string, string>>(text, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader,
      complete: (results) => {
        const headers = results.meta.fields ?? [];
        if (headers.length === 0) {
          reject(new Error("No header row found in the CSV file."));
          return;
        }
        options.onProgress?.(1);
        resolve({ headers, rows: results.data });
      },
      error: (error: Error) => reject(error),
    });
  });
}
